import { DatePickerProvider, useDatePickerContext } from '@rehookify/datepicker';
import React, { useEffect, useMemo, useState } from 'react';
import { useMediaQueryContext } from '../../providers/MediaQueryProvider';
import * as S from './Calendar.styled';
import { CalendarMonth } from './CalendarMonth';
import { CalendarTimeInput } from './CalendarTimeInput';
import { useTime } from './hooks/useTime';
import { getInitialSelectedTimes } from './utils/getInitialSelectedTimes';
import { setTimeToDate } from './utils/setTimeToDate';
const CalendarContent = ({ selectedDates, showTimeInputs, selectedTimes, setSelectedTimes, minDate, maxDate, timeLabels, footer, bare, }) => {
    const { data: { calendars }, } = useDatePickerContext();
    const { isValid } = useTime();
    const newDatesWithTimes = useMemo(() => {
        return selectedDates.map((date, index) => {
            const time = selectedTimes[index];
            return setTimeToDate(date, isValid(time) ? time : undefined);
        });
    }, [selectedDates, selectedTimes]);
    const isMinDateValid = (date) => {
        if (!minDate || !date)
            return true;
        return date > minDate;
    };
    const isMaxDateValid = (date) => {
        if (!maxDate || !date)
            return true;
        return date < maxDate;
    };
    const isTimeValid = (time, index) => {
        if (!showTimeInputs)
            return true;
        return (isValid(time) &&
            isMinDateValid(newDatesWithTimes[index]) &&
            isMaxDateValid(newDatesWithTimes[index]));
    };
    const areSelectedTimesValid = useMemo(() => {
        return !selectedTimes.some((time, index) => !isTimeValid(time, index));
    }, [selectedTimes]);
    const handleTimeChange = (value, index) => {
        const updatedTimes = [...selectedTimes];
        updatedTimes[index] = value;
        setSelectedTimes(updatedTimes);
    };
    return (React.createElement(S.Container, { bare: bare },
        React.createElement(S.MonthsContainer, null, calendars.map((calendar, index) => (React.createElement(CalendarMonth, { key: calendar.month, calendar: calendar, showPrevMonthButton: index === 0, showNextMonthButton: index === calendars.length - 1 })))),
        showTimeInputs && (React.createElement(S.TimeContainer, null, selectedTimes.map((time, index) => (React.createElement(CalendarTimeInput, { key: index, value: time, label: timeLabels[index], isValid: isTimeValid(time, index), onValueChange: (value) => handleTimeChange(value, index) }))))),
        footer?.({ selectedTimes, areSelectedTimesValid })));
};
export const Calendar = ({ selectedDates, onDatesChange, type, showTimeInputs = true, minDate, maxDate, timeLabels, initialTimeValues, footer, bare, }) => {
    const media = useMediaQueryContext();
    const calendarOffsets = type === 'single' || !media.tablet ? [] : [1];
    const [selectedTimes, setSelectedTimes] = useState(initialTimeValues || getInitialSelectedTimes(selectedDates, type));
    const { isValid } = useTime();
    useEffect(() => {
        handleDatesChange(selectedDates);
    }, [selectedTimes]);
    const handleDatesChange = (dates) => {
        if (!showTimeInputs) {
            onDatesChange?.(dates);
        }
        const datesWithTime = dates.map((date, index) => {
            const time = selectedTimes[index] || '';
            return setTimeToDate(date, isValid(time) ? time : undefined);
        });
        onDatesChange?.(datesWithTime);
    };
    const getTimeLabels = () => {
        if (timeLabels) {
            return timeLabels;
        }
        if (type === 'single') {
            return ['Time'];
        }
        return ['Start time', 'End time'];
    };
    return (React.createElement(DatePickerProvider, { config: {
            selectedDates,
            onDatesChange: handleDatesChange,
            calendar: {
                offsets: calendarOffsets,
                mode: 'fluid',
            },
            dates: {
                mode: type,
                minDate,
                maxDate,
                toggle: false,
                selectSameDate: true,
            },
            locale: {
                day: 'numeric',
            },
        } },
        React.createElement(CalendarContent, { selectedDates: selectedDates, showTimeInputs: showTimeInputs, minDate: minDate, maxDate: maxDate, selectedTimes: selectedTimes, setSelectedTimes: setSelectedTimes, timeLabels: getTimeLabels(), footer: footer, bare: bare })));
};
